import type { ReactNode } from 'react';
import moment from 'moment/moment';
import { getKnowledgeBaseConfig, setKnowledgeBaseConfig } from 'src/utils/pageStorageConfig';
import { uuid } from 'src/utils/uuid';
import { FILE_STATE } from 'src/constants/knowledge-base';

const DATE_FORMAT = 'll';

export interface AutoSyncStatus {
  status: string;
  color: string;
}

export interface TreeNode {
  id?: string;
  name: string;
  type: string;
  path: string;
  root?: boolean;
  parent?: TreeNode | false | string;
  children?: TreeNode[];
  visible?: boolean;
  checked?: boolean;
  disabled?: boolean;
  autosync?: AutoSyncStatus;
  localDate?: string;
  updated_at?: string;
  created_at?: string;
  state?: string;
  collector_id?: string | null;
  data?: Integration;
  urls?: string[];
  newPath?: string;
  // Source link of a website page or an imported file.
  url?: string;
  external_file_id?: string;
  // Set by the breadcrumb for its path items.
  icon?: ReactNode;
  [key: string]: unknown;
}

export interface Integration {
  id: string;
  name: string;
  type: string;
  // Ticket collectors' filters and a website collector's pages.
  start_from?: string;
  agents?: string[];
  tags?: string[];
  groups?: string[];
  urls?: string[];
  // Website collector settings.
  url?: string;
  exclude_urls?: string[];
  recursive?: boolean;
  enabled?: boolean;
  schedule?: { enabled?: boolean; interval?: number; period?: string };
  updated_at?: string;
  created_at?: string;
  // Ticket collectors' helpdesk subdomain; the API key is only sent when it changes.
  subdomain?: string;
  api_key?: string;
  // Zendesk Help Center collector settings; excluded articles and source links are URLs.
  domain?: string;
  zendesk_subdomain?: string;
  zendesk_email?: string;
  brand_id?: string;
  collect_private_articles?: boolean;
  excluded_articles?: string[];
  source_links?: string[];
  // The admin email of a collector connected with an API token.
  auth_email?: string;
  // Zendesk OAuth state: auth_type 'oauth' or 'api_token', auth_status 'ok' once authorized.
  // The API also repeats it inside `data`; read it here.
  auth_type?: string;
  auth_status?: string;
  // Collector-specific settings.
  data?: { auth_status?: string; [key: string]: unknown };
  [key: string]: unknown;
}

export interface KBFile {
  id?: string | number;
  name: string;
  download_url?: string;
  path?: string;
  state?: string;
  collector_id?: string | null;
  updated_at?: string;
  [key: string]: unknown;
}

export const SORT_MAP = {
  type: 'By Type',
  name: 'By Name',
  date: 'By Date',
} as const;

export const SORT_ORDER: Record<string, number> = {
  integration: 40,
  website: 30,
  folder: 20,
  FAQ: 10,
  file: 0,
};

export const INTEGRATION_NAMES_MAP: Record<string, string> = {
  zendesk_helpcenter: 'Zendesk Help Center',
  zendesk_tickets: 'Zendesk Tickets',
  freshdesk_helpcenter: 'Freshdesk Help Center',
  freshdesk_tickets: 'Freshdesk Tickets',
  web_scrapper: 'Website',
};

export const AUTOSYNC_STATUSES_MAP = {
  unavailable: {
    status: 'Unavailable',
    color: '#C1CACB',
  },
  active: {
    status: 'Active',
    color: '#33FF71',
  },
  paused: {
    status: 'Paused',
    color: '#FFBA33',
  },
  failed: {
    status: 'Failed',
    color: '#FF3F33',
  },
} as const;

const getIntegrationAutoSyncStatus = (integration: Integration): AutoSyncStatus => {
  const map = AUTOSYNC_STATUSES_MAP;

  switch (integration.type) {
    case 'zendesk_helpcenter':
      return integration?.enabled ? map.active : map.paused;
    case 'zendesk_tickets':
    case 'freshdesk_tickets':
    case 'web_scrapper': {
      return integration?.schedule?.enabled ? map.active : map.paused;
    }
    default: {
      return map.active;
    }
  }
};

export const getInitialTree = (): TreeNode => {
  return {
    name: 'Knowledge Base',
    type: 'folder',
    path: '/',
    root: true,
    parent: false,
    children: [],
  };
};

export const getFileExtension = (path: string): string => {
  const cleaned = path.replace(/[\\/]/g, '');
  const match = /\.([^.]+)$/.exec(cleaned);

  return match ? match[1] : '';
};

function findWhere(array: TreeNode[], key: string, value: unknown): TreeNode | false {
  let t = 0;
  while (t < array.length && (array[t] as Record<string, unknown>)[key] !== value) {
    t++;
  }

  return t < array.length ? array[t] : false;
}

export const getFoldersStructure = (
  files: KBFile[] = [],
  integrations: Integration[] = [],
): TreeNode => {
  const tree = getTreeFromFiles(files);
  addIntegrationsToTree(files, tree, integrations);

  tree.root = true;
  mergeWithEmptyFolders(tree);
  setUpdatedAtFolders(tree);

  return tree;
};

export const addIntegrationsToTree = (
  files: KBFile[] | null,
  tree: TreeNode,
  integrations: Integration[],
): void => {
  files = files || [];

  const rootChildren = tree.children!;

  integrations.forEach((integration) => {
    const autosync = getIntegrationAutoSyncStatus(integration);
    const treeItem: TreeNode = {
      name: integration.name,
      parent: tree,
      type: integration.type === 'web_scrapper' ? 'website' : 'integration',
      autosync: autosync,
      disabled: false,
      visible: true,
      path: `/${integration.name}`.replace(/\/\//, '/'),
      localDate: getLocalFileDate(integration.updated_at),
      id: integration.id,
      updated_at: integration.updated_at,
      created_at: integration.created_at,
      data: integration,
      children: [],
    };

    treeItem.children = files
      .filter((file) => {
        return file.collector_id === integration.id;
      })
      .map((file) => {
        return {
          ...file,
          parent: treeItem,
          type: treeItem.type,
          autosync: autosync,
          visible: true,
          disabled: file.state !== 'uploaded',
          path: `${treeItem.path}/${file.name}`.replace(/\/\//, '/'),
          localDate: getLocalFileDate(file.updated_at),
        } as TreeNode;
      });

    rootChildren.push(treeItem);
  });
};

const createFileNode = (file: KBFile, parent: TreeNode | undefined, ext: string): TreeNode =>
  ({
    id: `${file.name}-${parent?.name || ''}-${uuid()}`,
    ...file,
    parent,
    name: ext === 'faq' ? file.name.split(`.${ext}`)[0].trim() : file.name,
    type: ext === 'faq' ? 'FAQ' : 'file',
    visible: true,
    autosync: AUTOSYNC_STATUSES_MAP.unavailable,
    disabled: file.state !== 'uploaded',
    path: `${file.path || ''}/${file.name}`.replace(/\/\//, '/'),
    localDate: getLocalFileDate(file.updated_at),
  }) as TreeNode;

// Paths join folder names with "/", so a "/" (and the "%" escape itself) inside a folder name is
// escaped. Node names keep the plain name; node paths and stored file paths hold escaped segments.
export const encodePathSegment = (name: string): string =>
  name.replace(/%/g, '%25').replace(/\//g, '%2F');

export const decodePathSegment = (segment: string): string =>
  segment.replace(/%(2F|25)/gi, (match) => (match === '%25' ? '%' : '/'));

const createFolderNode = (part: string, parent: TreeNode | undefined): TreeNode => ({
  id: `${part}-${uuid()}`,
  name: part,
  parent,
  type: 'folder',
  visible: true,
  autosync: AUTOSYNC_STATUSES_MAP.unavailable,
  path: parent ? `${parent.path}/${encodePathSegment(part)}`.replace(/\/\//, '/') : '/',
  children: [],
});

// Walks the file's folder path from the root, creating missing folders, and adds the file.
const addFileToTree = (tree: TreeNode[], file: KBFile) => {
  const isInRoot = !file.path || file.path === '/';
  const path = isInRoot ? [''] : `/${file.path}`.replace(/\/\//, '/').split('/');
  let currentLevel: TreeNode[] = tree;
  let parent: TreeNode | undefined;

  path.push(file.name);

  for (let j = 0; j < path.length; j++) {
    const isLastItem = j === path.length - 1;
    const part = isLastItem ? path[j] : decodePathSegment(path[j]);
    const existingPath = findWhere(currentLevel, 'name', part);

    if (existingPath) {
      if (isLastItem) {
        currentLevel.push(createFileNode(file, parent, getFileExtension(part)));
      }

      currentLevel = existingPath.children!;
      parent = existingPath;
      continue;
    }

    const newPart = isLastItem
      ? createFileNode(file, parent, getFileExtension(part))
      : createFolderNode(part, parent);

    if (file.collector_id === null) {
      currentLevel.push(newPart);
      currentLevel = newPart.children!;
      parent = newPart;
    }
  }
};

export const getTreeFromFiles = (files: KBFile[]): TreeNode => {
  const tree: TreeNode[] = [];

  for (const file of files) {
    addFileToTree(tree, file);
  }

  return tree.length ? tree[0] : getInitialTree();
};

// Sorts in place (callers rely on the mutation) and returns the same array.
export const sortByType = (array: TreeNode[]): TreeNode[] => {
  array.sort((a, b) => a.name.localeCompare(b.name));
  array.sort((a, b) => {
    if (SORT_ORDER[a.type] > SORT_ORDER[b.type]) {
      return -1;
    } else {
      return 1;
    }
  });
  array.sort((a, b) => {
    if (SORT_ORDER[a.type] === SORT_ORDER[b.type]) {
      return a.name.localeCompare(b.name);
    } else {
      return 0;
    }
  });

  return array;
};

export const sortByName = (array: TreeNode[]): TreeNode[] => {
  return array.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
};

// Sorts in place (callers rely on the mutation) and returns the same array.
export const sortByDate = (array: TreeNode[]): TreeNode[] => {
  array.sort((a, b) => {
    return (
      Number(moment(b.updated_at).format('YYYYMMDD')) -
      Number(moment(a.updated_at).format('YYYYMMDD'))
    );
  });
  array.sort((a) => (a.type === 'folder' ? -1 : 1));

  return array;
};

export const createFolder = (name: string, parent: TreeNode): TreeNode => {
  const folder: TreeNode = {
    id: `${name}-${uuid()}`,
    name,
    parent: parent,
    type: 'folder',
    visible: true,
    autosync: AUTOSYNC_STATUSES_MAP.unavailable,
    path: `${parent ? parent.path : ''}/${encodePathSegment(name)}`.replace(/\/\//, '/'),
    localDate: getLocalFileDate(),
    children: [],
  };
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  const isLocalExist = (config.emptyFolders as TreeNode[]).some(
    (emptyFolder) => emptyFolder.path === folder.path,
  );

  if (!isLocalExist) {
    const localFolder = { ...folder, parent: parent.path };

    (config.emptyFolders as TreeNode[]).push(localFolder as TreeNode);
    setKnowledgeBaseConfig(config);
  }

  return folder;
};

export const createFile = (file: KBFile, parent: TreeNode): TreeNode => {
  const ext = getFileExtension(file.name);
  const isFAQ = ext === 'faq';

  return {
    id: `${file.name}-${parent.name}-${uuid()}`,
    ...file,
    parent,
    visible: true,
    name: isFAQ ? file.name.split(`.${ext}`)[0].trim() : file.name,
    type: isFAQ ? 'FAQ' : 'file',
    autosync: parent?.autosync || AUTOSYNC_STATUSES_MAP.unavailable,
    path: `${file.path}/${file.name}`,
    localDate: getLocalFileDate(file.updated_at),
  } as TreeNode;
};

export const updateBranchInTree = (
  tree: TreeNode,
  branch: TreeNode,
  newBranch: Partial<TreeNode>,
): TreeNode | void => {
  if (tree.path === branch.path) {
    Object.assign(tree, { ...newBranch });
    mergeWithEmptyFolders(tree);

    return tree;
  } else {
    for (const child of tree.children!) {
      if (child.type === 'folder') {
        updateBranchInTree(child, branch, newBranch);
      }
    }
  }
};

export const findBranch = (tree: TreeNode, path: string): TreeNode | false | undefined => {
  if (!path) {
    return false;
  }

  if (tree.path === path || tree.urls?.[0] === path) {
    return tree;
  }

  if (tree.children?.length) {
    for (const child of tree.children) {
      const is = {
        folder: child.type === 'folder',
        website: child.type === 'website' && !child.collector_id,
        integration: child.type === 'integration' && !child.collector_id,
      };

      if (is.folder || is.integration || is.website) {
        const result = findBranch(child, path);

        if (result) {
          return result;
        }
      }
    }
  }
};

export const findActiveBranch = (tree: TreeNode): TreeNode | false | undefined => {
  const { activeBranchPath } = getKnowledgeBaseConfig() as { activeBranchPath?: string };

  if (!activeBranchPath) {
    return false;
  }

  return findBranch(tree, activeBranchPath);
};

export const getLocalFileDate = (
  date: Date | string = new Date(),
  format: string = DATE_FORMAT,
): string => {
  const utcDate = moment.utc(date).toDate();

  return moment(utcDate).format(format);
};

export const getFoldersArrayFromTree = (tree: TreeNode): TreeNode[] => {
  let folders: TreeNode[] = [];

  if (tree.type === 'folder') {
    folders.push(tree);
  }

  if (tree.children?.length) {
    for (const children of tree.children) {
      if (children.type === 'folder') {
        const childFolders = getFoldersArrayFromTree(children);

        folders = [...folders, ...childFolders];
      }
    }
  }

  return folders;
};

export const getCheckedFiles = (branch: TreeNode): TreeNode[] => {
  let folders: TreeNode[] = [];

  if (branch.visible && branch.checked) {
    folders.push(branch);
  }

  for (const children of branch.children!) {
    if (children.visible && children.type === 'folder') {
      const childFolders = getCheckedFiles(children);

      folders = folders.concat(childFolders);
    } else if (children.visible && children.checked) {
      folders.push(children);
    }
  }

  return folders;
};

export const setCheckedChildren = (branch: TreeNode, checked?: boolean, parent = true): void => {
  checked = checked ?? branch.checked;

  if (branch.visible && !branch.root && branch.type !== 'integration' && !parent) {
    branch.checked = checked;
  }

  for (const children of branch.children!) {
    if (children.visible && children.type !== 'integration') {
      children.checked = checked;
    }

    if (children.visible && children.type === 'folder') {
      setCheckedChildren(children, checked, false);
    }
  }
};

export const updateCheckedPaths = (files: TreeNode[], path: string): TreeNode[] => {
  return files.map((file) => {
    // File names are the last segment and stay as is; removeFileName strips them again.
    const segment = file.type === 'folder' ? encodePathSegment(file.name) : file.name;

    if (file.parent && typeof file.parent !== 'string' && file.parent.checked) {
      file.newPath = `${file.parent.newPath}/${segment}`;
    } else {
      file.newPath = `${path}/${segment}`;
    }

    file.newPath = file.newPath.replace(/\/\//, '/');

    return file;
  });
};

/** The folder containing `item`, or undefined for the root (whose parent is `false`). */
export const getParentFolder = (item: TreeNode): TreeNode | undefined =>
  item.parent && typeof item.parent !== 'string' ? item.parent : undefined;

export const getRootFolder = (item: TreeNode): TreeNode | undefined => {
  let result: TreeNode | undefined;

  if (item.root) {
    result = item;
  }

  if (item.parent && typeof item.parent !== 'string') {
    result = getRootFolder(item.parent);
  }

  return result;
};

export const getChildrenFiles = (item: TreeNode): TreeNode[] => {
  let files: TreeNode[] = [];

  if (item.children?.length) {
    for (const children of item.children) {
      if (children.type === 'folder') {
        const childFolders = getChildrenFiles(children);

        files = files.concat(childFolders);
      } else {
        files.push(children);
      }
    }
  }

  return files;
};

export const getItemsByName = (name = '', branch: TreeNode): TreeNode[] => {
  let items: TreeNode[] = [];

  for (const children of branch.children!) {
    if (['folder', 'integration', 'website'].includes(children.type) && children.children) {
      const childFolders = getItemsByName(name, children);

      items = items.concat(childFolders);
    }

    if (children.name.toLowerCase().includes(name.trim().toLowerCase())) {
      items.push(children);
      continue;
    }

    if (children.url && typeof children.url === 'string') {
      if (children.url.toLowerCase().includes(name.trim().toLowerCase())) {
        items.push(children);
      }
    }
  }

  return items;
};

export const filterBranchByName = (name: string, branch: TreeNode): TreeNode => {
  const newBranch = { ...branch };
  newBranch.children = getItemsByName(name, newBranch);

  return newBranch;
};

export const getParentsFolders = (branch: TreeNode): TreeNode[] => {
  let items: TreeNode[] = [];

  items.unshift(branch);

  if (branch.parent && typeof branch.parent !== 'string') {
    const parent = getParentsFolders(branch.parent);
    items = parent.concat(items);
  }

  return items;
};

export const setUpdatedAtFolders = (branch: TreeNode): void => {
  if (branch.children) {
    for (const child of branch.children) {
      setUpdatedAtFolders(child);
    }
  }

  if (branch.parent && typeof branch.parent !== 'string') {
    if (branch.parent.updated_at) {
      const isAfter = moment(branch.updated_at).isSameOrAfter(branch.parent.updated_at);

      branch.parent.updated_at = isAfter ? branch.updated_at : branch.parent.updated_at;
    } else {
      branch.parent.updated_at = branch.updated_at;
    }

    branch.parent.localDate = getLocalFileDate(branch.parent.updated_at);
  }
};

export const mergeWithEmptyFolders = (branch: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;
  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  (config.emptyFolders as TreeNode[]).forEach((folder) => {
    const parent = findBranch(branch, folder.parent as string);

    if (parent && typeof parent !== 'boolean') {
      const child = parent.children!.find((child) => child.path === folder.path);

      if (child) {
        const childFiles = getChildrenFiles(child);
        if (childFiles.length) {
          config.emptyFolders = (config.emptyFolders as TreeNode[]).filter(
            (emptyFolder) => emptyFolder.path !== folder.path,
          );

          setKnowledgeBaseConfig(config);
        }
      } else {
        parent.children!.push({ ...folder, parent });
      }
    }
  });
};

export const setVisibilityByTypes = (branch: TreeNode, types: Array<{ type: string }>): void => {
  branch?.children?.forEach((child) => {
    if (child.type === 'folder') {
      child.visible = true;

      return;
    }

    child.visible = types.some(({ type }) => type.toLowerCase() === child.type.toLowerCase());
  });
};

export const removeEmptyFolder = (folder: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = ((config.emptyFolders as TreeNode[]) || []).filter(
    (emptyFolder) =>
      emptyFolder.path !== folder.path && !emptyFolder.path.startsWith(`${folder.path}/`),
  );

  setKnowledgeBaseConfig(config);
};

export const removeEmptyFolders = (folders: TreeNode[]): TreeNode[] => {
  folders.forEach(removeEmptyFolder);

  return (getKnowledgeBaseConfig().emptyFolders as TreeNode[]) || [];
};

// Folders exist only through their files' paths, so remember the parents of files being deleted
// as empty folders. Folders deleted along with the files (and anything inside them) are skipped.
export const preserveParentFolders = (items: TreeNode[]): void => {
  const deletedFolderPaths = items
    .filter((item) => item.type === 'folder')
    .map((item) => item.path);
  const isDeleted = (path: string) =>
    deletedFolderPaths.some(
      (deletedPath) => path === deletedPath || path.startsWith(`${deletedPath}/`),
    );
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;
  const emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  items
    .filter((item) => item.type !== 'folder')
    .forEach((file) => {
      // Root first, so each parent is already listed when mergeWithEmptyFolders attaches a child.
      getParentsFolders(file)
        .slice(0, -1)
        .filter((folder) => folder.type === 'folder' && !folder.root && !isDeleted(folder.path))
        .forEach((folder) => {
          if (emptyFolders.some((emptyFolder) => emptyFolder.path === folder.path)) {
            return;
          }

          emptyFolders.push({
            ...folder,
            parent: (getParentFolder(folder)?.path ?? '/') as string,
            children: [],
            checked: false,
          });
        });
    });

  config.emptyFolders = emptyFolders;
  setKnowledgeBaseConfig(config);
};

export const addFolderToEmptyFolders = (branch: TreeNode): void => {
  if (branch.type === 'folder') {
    const newFolder: Record<string, unknown> = {
      ...branch,
      children: [],
      path: branch.newPath || branch.path,
      checked: false,
    };

    if (branch.parent) {
      const parent = (newFolder.path as string).split('/');

      newFolder.parent = parent.slice(0, -1).join('/') || '/';
    }

    const config = getKnowledgeBaseConfig() as Record<string, unknown>;

    config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

    const isEmptyFolderExist = (config.emptyFolders as TreeNode[]).some(
      (emptyFolder) => emptyFolder.path === newFolder.path,
    );

    if (!newFolder.root && !isEmptyFolderExist) {
      config.emptyFolders = removeEmptyFolders([branch]);
      (config.emptyFolders as TreeNode[]).push(newFolder as TreeNode);
      setKnowledgeBaseConfig(config);
    }
  }

  if (branch.parent && typeof branch.parent !== 'string') {
    addFolderToEmptyFolders(branch.parent);
  }
};

export const editEmptyFolder = (name: string, item: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  const emptyFolder = (config.emptyFolders as TreeNode[]).find(
    (folder) => folder.path === item.path,
  );
  const pathParts = item.path.split('/');

  const newPath = pathParts.slice(0, -1).join('/') || '/';
  if (emptyFolder) {
    emptyFolder.name = name;
    emptyFolder.path = `${newPath}/${encodePathSegment(name)}`.replace(/\/\//, '/');
  }
  setKnowledgeBaseConfig(config);
};

export const isUploadingState = (item: Pick<TreeNode, 'state' | 'children'>): boolean => {
  if (item.state === FILE_STATE.UPLOADING || item.state === FILE_STATE.DELETING) {
    return true;
  }

  if (item.children?.length) {
    for (const children of item.children) {
      const isUploading = isUploadingState(children);

      if (isUploading) {
        return true;
      }
    }
  }

  return false;
};

export const removeFileName = (path: string, fileName: string) => {
  if (!path || !fileName) return path || '';

  const escape = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

  return path.replace(/\\/g, '/').replace(new RegExp(`/${escape(fileName)}$`), '');
};

export function generateUniqueName(name: string, items: { name: string }[]) {
  const existingNames = new Set(items.map((item) => item.name));

  if (!existingNames.has(name)) {
    return name;
  }

  let count = 1;
  let uniqueName = `${name}-${count}`;

  while (existingNames.has(uniqueName)) {
    count += 1;
    uniqueName = `${name}-${count}`;
  }

  return uniqueName;
}
